import {entry, fillArray, noop, objectFromEntries} from '@benchling/js-utils';
import {Color} from '@benchling/nucleus-ui';
import produce from 'immer';
import React from 'react';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {
  ProcedureEdgeType,
  ProcedureParameterConfirmationMethod,
  ProcedureType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {SimpleTableEditable, standaloneProps} from 'notebook/simple-tables/SimpleTableEditable';
import {EvaluatedSimpleTableDoc, useSimpleUI} from 'notebook/simple-tables/useSimpleEvaluation';
import {ColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import toColumnConstraint from 'notebook/structured-tables/columns/toColumnConstraint';
import {toSchemaFieldColumnDefinition} from 'notebook/structured-tables/columns/toSchemaFieldColumnDefinition';
import {StructuredTableColumn} from 'notebook/structured-tables/StructuredTableConstants';
import {StructuredTableKind} from 'notebook/structured-tables/StructuredTableKind';
import {countBlockingValidationErrors} from 'notebook/structured-tables/util/hasBlockingValidationErrors';
import kindToIcon from 'notebook/structured-tables/util/kindToIcon';
import {
  makeSchemaFieldStructuredColumn,
  makeStructuredTableColumn,
} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {Cell} from 'notebook/tables/constants/CellConstants';
import {CustomEvalResult} from 'notebook/tables/constants/CustomEvalConstants';
import {Cells, RowData} from 'notebook/tables/constants/RowConstants';
import {ColumnConstraint, TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {Table} from 'notebook/tables/constants/TableConstants';
import ErrorTriangle, {TriangleLocation, TriangleState} from 'notebook/tables/errors/ErrorTriangle';
import {EvaluatedTable, FullTableMap, OutputTableMap} from 'notebook/tables/excess/EvaluationConstants';
import {areTypedValuesEquivalent, isTypedValueEmpty} from 'notebook/tables/typed-values/DataTypes';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {ResolveQueryFn, ResolveTypedValueToTypeFn} from 'notebook/tables/typed-values/TypedValueResolution';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import {
  getConfirmationMethodStructuredTableColumn,
  getConfirmationMethodValueFromCell,
  makeConfirmationMethodCell,
} from 'procedures/tables/procedureTableAdapterUtils';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {
  ExperimentFlowchart,
  ExperimentMethod,
} from 'studies/typed/shared/procedure-runs/design-state/ExperimentalInputs';
import {
  BaseCondition,
  Condition,
  getMethodIdsImplementingConditionGroup,
  isConditionGroup,
  isPersistedCondition,
} from 'studies/typed/shared/procedure-runs/design-state/useExperimentDesignState';
import {shouldShowFlowchartProcedureRunDesign} from 'studies/typed/shared/procedure-runs/design-tabs/ProcedureRunDesignUtils';
import {getProcedureRunConditionTextForProcedureType} from 'studies/TypedStudyUtils';
import {NEAR_BLACK} from 'ui-library/Colors';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import * as logging from 'util/Logging';

import {
  getDefaultLabelByComponentNameAndProcedureType,
  getEquipmentModelsLabelByProcedureType,
  TABLE_NAME_BY_COMPONENT_NAME,
} from './ProcedureExperimentConstants';
import {
  ExperimentalInputEvaluatedValues,
  ExperimentalInputs,
  ExperimentalInputsComponentName,
  ExperimentalInputsContextProperties,
  ExperimentalInputsPerMethod,
  ExperimentalInputsValuesPerCondition,
  ParameterFields,
  ParameterFieldValues,
} from './ProcedurePlanTypes';
import {
  validateEquipment,
  validateMaterialInputConfirmationMethod,
  validateNumericFieldValue,
  validateValueRange,
  validateArchivedEntity,
  validateNumberOfValues,
} from './validateInputTable';
import VaryingParameterName from './VaryingParameterName';

const CELL_ALIGNMENT = 'left';
// Experimental input tables are rendered via `SimpleTableEditable` (not the procedure config structured
// table stack), but we intentionally derive icons from matching `StructuredTableKind` values so header
// icons stay in sync with method editor definition tables.
const INPUT_TABLE_KIND_BY_COMPONENT_NAME: Record<ExperimentalInputsComponentName, StructuredTableKind> = {
  [ExperimentalInputsComponentName.PARAMETERS]: StructuredTableKind.PROCEDURE_DATA_PARAMETERS_CONFIG,
  [ExperimentalInputsComponentName.MATERIAL_INPUTS]: StructuredTableKind.PROCEDURE_MATERIAL_INPUTS,
  [ExperimentalInputsComponentName.EQUIPMENT]: StructuredTableKind.PROCEDURE_EQUIPMENT,
};

/**
 * Evaluated filtered table data for presenting a table filtered to only some rows, and original parameter
 * indices needed to map edits made in the filtered view back to the complete table.
 */
export interface FilteredTableView {
  /**
   * Evaluated, filtered table document to render (rather than the unfiltered `context.simpleTableDoc`).
   */
  simpleTableDoc: EvaluatedSimpleTableDoc;
  /**
   * Original-table parameter indices included in the filtered table, in rendered row order.
   */
  visibleParameterIndices: ReadonlyArray<number>;
}

/**
 * A hook for managing the state needed for any Parameter+ InputTable.
 * @returns a SimpleTableEditable JSX.Element
 */
export function useInputTable(
  componentName: ExperimentalInputsComponentName,
  {
    context,
    selectedMethod,
    filteredTableView,
  }: {
    context: ExperimentalInputsContextProperties;
    selectedMethod: ExperimentMethod;
    lockedReasonByInputId?: Record<string, string> | null;
    procedureType: ProcedureType;
    filteredTableView?: FilteredTableView;
  }
): JSX.Element | null {
  const experimentalInputs = context.draftInputs[selectedMethod.id];
  const visibleConditionIndices = getVisibleConditionIndicesForMethod(context.conditions, selectedMethod.id);
  const tableName = getTableName(selectedMethod.id, componentName);
  const simpleTableDoc = filteredTableView ? filteredTableView.simpleTableDoc : context.simpleTableDoc;
  const enhancedProps = useSimpleUI(tableName, simpleTableDoc, true);
  if (enhancedProps == null) {
    return null;
  }
  return (
    <SimpleTableEditable
      {...standaloneProps}
      {...enhancedProps}
      dataTestAttrs={{component: 'ExperimentalInputTable', key: componentName}}
      hideFormulaBox={true}
      isCompactBottomSpacing={true}
      isCollapsible={true}
      isLeftAlignRowHeader={true}
      icon={getInputTableHeaderIcon(componentName)}
      locked={context.locked}
      cid={`${simpleTableDoc.cid}-${tableName}`}
      updateHandler={(index: number, {table}: {table: Table}) => {
        const {newExperimentalInputs, newConfirmationMethods} = convertTableToExperimentalInputs(
          experimentalInputs.values,
          experimentalInputs.fields[componentName],
          experimentalInputs.defaultValues[componentName],
          table.table,
          table.columnLabels,
          componentName,
          experimentalInputs.parameterConfirmationMethods,
          visibleConditionIndices,
          filteredTableView?.visibleParameterIndices
        );
        context.setExperimentalInputs(selectedMethod.id, newExperimentalInputs, newConfirmationMethods);
      }}
      storeState={noop}
      restoreState={noop}
      canRename={false}
    />
  );
}

function getTableName(methodId: string, componentName: ExperimentalInputsComponentName): string {
  return `${methodId}_${componentName}`;
}

function getInputTableHeaderIcon(componentName: ExperimentalInputsComponentName): BenchlingIconName {
  return kindToIcon(INPUT_TABLE_KIND_BY_COMPONENT_NAME[componentName]);
}

function getHasFieldColumn(
  experimentalInputs: ExperimentalInputsPerMethod,
  componentName: ExperimentalInputsComponentName,
  visibleParameterIndices?: ReadonlyArray<number>
): boolean {
  const parameterIndices =
    visibleParameterIndices ?? getDenseParameterIndices(experimentalInputs.fields[componentName].length);
  return parameterIndices.some(
    (parameterIndex) => experimentalInputs.fields[componentName][parameterIndex].length > 1
  );
}

function getHasConfirmationMethodColumn(componentName: ExperimentalInputsComponentName): boolean {
  return (
    BenchlingConfig.PROCEDURES_ENABLE_USE_PARAMETERS_IN_PLATES &&
    [ExperimentalInputsComponentName.PARAMETERS, ExperimentalInputsComponentName.MATERIAL_INPUTS].includes(
      componentName
    )
  );
}

function getHasEquipmentModelsColumn(componentName: ExperimentalInputsComponentName): boolean {
  return componentName === ExperimentalInputsComponentName.EQUIPMENT;
}

export function getNumColumnsBeforeConditions(
  experimentalInputs: ExperimentalInputsPerMethod,
  componentName: ExperimentalInputsComponentName
): number {
  const numAlwaysPresentColumns = 2; // Name and Setpoint/Default
  return (
    numAlwaysPresentColumns +
    (getHasFieldColumn(experimentalInputs, componentName) ? 1 : 0) +
    (getHasEquipmentModelsColumn(componentName) ? 1 : 0)
  );
}

export function getNumTrailingColumns(componentName: ExperimentalInputsComponentName): number {
  return getHasConfirmationMethodColumn(componentName) ? 1 : 0;
}

/**
 * Returns a mapping from parameter+ id to the reason why the value is locked.
 */
export function getLockedMaterialInputReasons(
  method: ExperimentMethod,
  procedureFlowchart: ExperimentFlowchart,
  procedureType: ProcedureType
): {[cid: string]: string} {
  const inboundMaterialIds = new Set(
    procedureFlowchart.edges
      .filter((edge) => edge.type === ProcedureEdgeType.MATERIAL_EDGE)
      .map((edge) => edge.targetId)
  );

  const materialIds = method.materialInputSettings.map((material) => material.version.id);

  return objectFromEntries(
    materialIds
      .filter((materialId) => inboundMaterialIds.has(materialId))
      .map((materialId) => [
        materialId,
        `This material is mapped from another ${getProcedureUIString(
          ProceduresStringKeys.METHOD,
          procedureType,
          {stringCase: StringCase.LOWER}
        )} and cannot be varied in the experiment.`,
      ])
  );
}

export function getVaryingParameterIndices(
  experimentalInputs: ExperimentalInputsPerMethod,
  componentName: ExperimentalInputsComponentName,
  visibleConditionIndices?: ReadonlyArray<number>
): Array<number> {
  if (experimentalInputs.values.length === 0) {
    return [];
  }

  return experimentalInputs.fields[componentName].reduce<Array<number>>((varyingIndices, fields, index) => {
    if (getIsVaryingParameter(experimentalInputs, componentName, index, fields, visibleConditionIndices)) {
      varyingIndices.push(index);
    }
    return varyingIndices;
  }, []);
}

function getIsVaryingParameter(
  experimentalInputs: ExperimentalInputsPerMethod,
  componentName: ExperimentalInputsComponentName,
  parameterIndex: number,
  fields: ParameterFields[number],
  visibleConditionIndices?: ReadonlyArray<number>
): boolean {
  for (const {conditionValuesByComponent} of getExperimentalInputValuesByCondition(
    experimentalInputs.values,
    visibleConditionIndices
  )) {
    const conditionValues = conditionValuesByComponent[componentName][parameterIndex];
    const defaultValues = experimentalInputs.defaultValues[componentName][parameterIndex];
    if (
      fields.some(
        (_, fieldIndex) => !areTypedValuesEquivalent(conditionValues[fieldIndex], defaultValues[fieldIndex])
      )
    ) {
      return true;
    }
  }
  return false;
}

export function convertExperimentalInputsToTableMap(
  experimentalInputs: ExperimentalInputs,
  flowchart: ExperimentFlowchart,
  procedureType: ProcedureType,
  isLocked: boolean,
  conditions?: Array<BaseCondition>,
  allowEditAllConditions: boolean = false,
  archivedEntityIds?: ReadonlySet<string>
): FullTableMap {
  return objectFromEntries(
    Object.entries(experimentalInputs).flatMap(([methodId, inputsPerMethod]) => {
      const method = flowchart.nodes.find((m) => m.id === methodId);
      assert(method != null, 'Expected method to be found', {methodId, nodes: flowchart.nodes});
      const lockedReasonByInputId = getLockedMaterialInputReasons(method, flowchart, procedureType);
      return Object.values(ExperimentalInputsComponentName).flatMap((componentName) => {
        const table = convertExperimentalInputsToTable(
          inputsPerMethod,
          componentName,
          lockedReasonByInputId,
          conditions ?? [],
          procedureType,
          method,
          isLocked,
          allowEditAllConditions,
          archivedEntityIds
        );
        return table ? [entry(getTableName(methodId, componentName), table)] : [];
      });
    })
  );
}

/**
 * @package exported for testing
 */
export function convertTableToExperimentalInputs(
  currentExperimentalInputs: ExperimentalInputsValuesPerCondition,
  parameterFields: ParameterFields,
  defaultValues: ParameterFieldValues,
  tableCells: Cells,
  columnLabels: Array<string | null>,
  componentName: ExperimentalInputsComponentName,
  currentConfirmationMethods: Record<
    ExperimentalInputsComponentName,
    Array<ProcedureParameterConfirmationMethod>
  >,
  visibleConditionIndices: ReadonlyArray<number>,
  visibleParameterIndices?: ReadonlyArray<number>
): {
  newExperimentalInputs: ExperimentalInputsValuesPerCondition;
  newConfirmationMethods: Record<
    ExperimentalInputsComponentName,
    Array<ProcedureParameterConfirmationMethod>
  >;
} {
  // `visibleConditionIndices` is sourced from `conditions`, a separate array from
  // `currentExperimentalInputs`. Assert they stay in bounds so a length mismatch fails loudly here
  // instead of as a confusing "undefined" crash (or silently dropped edit) below.
  assert(
    visibleConditionIndices.every((conditionIndex) => conditionIndex < currentExperimentalInputs.length),
    'Expected visible condition indices to align with experimental input values',
    {visibleConditionIndices, valuesLength: currentExperimentalInputs.length}
  );
  const hasTrailingConfirmationMethod = columnLabels[columnLabels.length - 1] === 'Confirmation method';
  const numTrailing = hasTrailingConfirmationMethod ? 1 : 0;
  const newExperimentalInputs = produce(currentExperimentalInputs, (draftInputs) => {
    for (const {rowIndex, parameterIndex, fieldIndex} of enumTableRows(
      parameterFields,
      visibleParameterIndices
    )) {
      for (const {colIndex, conditionIndex} of enumTableParamValueCols(
        visibleConditionIndices,
        columnLabels.length,
        numTrailing
      )) {
        const cell = tableCells[rowIndex][colIndex];
        const value = cell.typedValue ?? {type: BaseTypedValueType.STRING, rawValue: cell.text};
        const defaultValue = defaultValues[parameterIndex][fieldIndex];
        // We re-hydrate the default value in place of any empty cell. This keeps the experimentalInputs free
        // from the notion that "empty implies default"
        draftInputs[conditionIndex][componentName][parameterIndex][fieldIndex] = !isTypedValueEmpty(value)
          ? value
          : defaultValue;
      }
    }
  });

  const confirmationMethodColumnIndex = hasTrailingConfirmationMethod ? columnLabels.length - 1 : -1;
  const newConfirmationMethods =
    confirmationMethodColumnIndex === -1
      ? currentConfirmationMethods // No column -> don't change confirmation methods
      : produce(currentConfirmationMethods, (draftConfirmationMethods) => {
          for (const {rowIndex, parameterIndex, fieldIndex} of enumTableRows(
            parameterFields,
            visibleParameterIndices
          )) {
            // Only first fieldIndex because cells are merged across fields of the same param
            if (fieldIndex === 0) {
              const confirmationMethodFromTable = getConfirmationMethodValueFromCell(
                tableCells[rowIndex][confirmationMethodColumnIndex]
              );
              draftConfirmationMethods[componentName][parameterIndex] = confirmationMethodFromTable;
            }
          }
        });

  return {newExperimentalInputs, newConfirmationMethods};
}

export function convertOutputTableMapToEvaluatedValues(
  outputTableMap: OutputTableMap,
  experimentalInputs: ExperimentalInputs,
  conditions: Array<BaseCondition>
): ExperimentalInputEvaluatedValues {
  return objectFromEntries(
    Object.entries(experimentalInputs).map(([methodId, methodInputs]) => {
      // `conditions` is a separate array from `methodInputs.values`; assert they stay in sync so a
      // length mismatch fails loudly here
      assert(
        conditions.length === 0 || conditions.length === methodInputs.values.length,
        'Expected conditions to align with experimental input values',
        {conditionsLength: conditions.length, valuesLength: methodInputs.values.length, methodId}
      );
      const visibleConditionIndices = getVisibleConditionIndicesForMethod(conditions, methodId);
      // Seed from the existing draft input values so conditions whose columns are hidden for this
      // method retain their values; only visible condition columns get overwritten below.
      const values = cloneExperimentalInputValuesPerCondition(methodInputs.values);

      // Then, enumerate the cells in the evaluated tables, and fill them into our values array.
      let blockingValueErrorCount = 0;
      Object.values(ExperimentalInputsComponentName).forEach((componentName) => {
        const tableName = getTableName(methodId, componentName);
        const outputTable: EvaluatedTable | null = outputTableMap[tableName];
        if (outputTable == null) {
          // See early return in convertExperimentalInputsToTable.
          assert(
            experimentalInputs[methodId].parameterNames[componentName].length === 0,
            'Expected no table only if there are no parameters'
          );
        } else {
          blockingValueErrorCount += countBlockingValidationErrors(outputTable);
          const fields = methodInputs.fields[componentName];
          const numTrailing = getNumTrailingColumns(componentName);
          for (const {rowIndex, parameterIndex, fieldIndex} of enumTableRows(fields)) {
            const numColumns = outputTable[0].length; // For loop body only hit when table has rows
            for (const {colIndex, conditionIndex} of enumTableParamValueCols(
              visibleConditionIndices,
              numColumns,
              numTrailing
            )) {
              values[conditionIndex][componentName][parameterIndex] ??= [];
              values[conditionIndex][componentName][parameterIndex][fieldIndex] =
                outputTable[rowIndex][colIndex].value;
            }
          }
        }
      });
      return entry(methodId, {blockingValueErrorCount, values});
    })
  );
}

function getDenseParameterIndices(numParameters: number): Array<number> {
  return Array.from({length: numParameters}, (_unused, index) => index);
}

function* enumTableRows(
  parameterFields: ParameterFields,
  visibleParameterIndices?: ReadonlyArray<number>
): Generator<{rowIndex: number; parameterIndex: number; fieldIndex: number}> {
  let rowIndex = 0;
  const parameterIndices = visibleParameterIndices ?? getDenseParameterIndices(parameterFields.length);
  for (const parameterIndex of parameterIndices) {
    assert(
      parameterIndex >= 0 && parameterIndex < parameterFields.length,
      'Expected visible parameter index to be in bounds',
      {
        parameterIndex,
        parameterFieldsLength: parameterFields.length,
        visibleParameterIndices,
      }
    );
    const numFields = parameterFields[parameterIndex].length;
    for (let fieldIndex = 0; fieldIndex < numFields; ++fieldIndex) {
      yield {rowIndex, parameterIndex, fieldIndex};
      rowIndex++;
    }
  }
}

function* enumTableParamValueCols(
  visibleConditionIndices: ReadonlyArray<number>,
  numColumns: number,
  numTrailingColumns: number = 0
): Generator<{colIndex: number; conditionIndex: number}> {
  const numNonConditionColumns = numColumns - visibleConditionIndices.length - numTrailingColumns;
  for (const [conditionColumnIndex, conditionIndex] of visibleConditionIndices.entries()) {
    yield {colIndex: conditionColumnIndex + numNonConditionColumns, conditionIndex};
  }
}

export function getDefaultConditions(length: number, procedureType: ProcedureType): Array<Condition> {
  return Array.from({length}, (_, conditionNumber: number) => ({
    numReplicates: 1,
    name: `${getProcedureRunConditionTextForProcedureType(procedureType, {capital: true})} ${
      conditionNumber + 1
    }`,
  }));
}

interface GetCellArgs {
  experimentalInputs: ExperimentalInputsPerMethod;
  componentName: ExperimentalInputsComponentName;
  parameterIndex: number;
  fieldIndex: number;
  lockedReasonByInputId: Record<string, string> | null;
  method: ExperimentMethod;
  isLockedPerCondition: Array<boolean>;
  shouldLockConfirmationMethod: boolean;
  isTableLocked: boolean;
  isVaryingParameter: boolean;
  archivedEntityIds?: ReadonlySet<string>;
  visibleParameterIndices?: ReadonlyArray<number>;
}

interface ColumnHandler {
  label: string;
  structuredTableColumn: StructuredTableColumn;
  getCellForFieldRow: (args: GetCellArgs) => Cell;
}

function getColumnHandlers(
  conditions: Array<BaseCondition>,
  visibleConditionIndices: ReadonlyArray<number>,
  experimentalInputs: ExperimentalInputsPerMethod,
  {
    componentName,
    procedureType,
    visibleParameterIndices,
  }: {
    componentName: ExperimentalInputsComponentName;
    procedureType: ProcedureType;
    visibleParameterIndices?: ReadonlyArray<number>;
  }
): Array<ColumnHandler> {
  const defaultSetpointColumnLabel = getDefaultLabelByComponentNameAndProcedureType(
    componentName,
    procedureType
  );

  const nameColumn: ColumnHandler = {
    label: 'Name',
    structuredTableColumn: makeStructuredTableColumn({columnType: TableColumnType.STRING, name: 'Name'}),
    getCellForFieldRow: makeParamNameCell,
  };

  const maybeConfirmationMethodColumn: ColumnHandler | null = getHasConfirmationMethodColumn(componentName)
    ? {
        label: 'Confirmation method',
        structuredTableColumn: getConfirmationMethodStructuredTableColumn(),
        getCellForFieldRow: makeParamConfirmationMethodCell,
      }
    : null;

  const maybeFieldColumn: ColumnHandler | null = getHasFieldColumn(
    experimentalInputs,
    componentName,
    visibleParameterIndices
  )
    ? {
        label: 'Field',
        structuredTableColumn: makeStructuredTableColumn({columnType: TableColumnType.STRING, name: 'Field'}),
        getCellForFieldRow: makeParamFieldCell,
      }
    : null;

  const maybeEquipmentModelsColumn: ColumnHandler | null = getHasEquipmentModelsColumn(componentName)
    ? {
        label: getEquipmentModelsLabelByProcedureType(procedureType),
        structuredTableColumn: makeStructuredTableColumn({
          columnType: TableColumnType.ROW_DELEGATE,
          name: getEquipmentModelsLabelByProcedureType(procedureType),
        }),
        getCellForFieldRow: makeEquipmentModelsCell,
      }
    : null;

  const defaultSetpointColumn: ColumnHandler = {
    label: defaultSetpointColumnLabel,
    structuredTableColumn: makeStructuredTableColumn({
      columnType: TableColumnType.ROW_DELEGATE,
      name: defaultSetpointColumnLabel,
    }),
    getCellForFieldRow: makeParamDefaultSetpointCell,
  };

  const conditionColumns: Array<ColumnHandler> = visibleConditionIndices.map((conditionIndex) => ({
    label: conditions[conditionIndex]?.name ?? '',
    structuredTableColumn: makeStructuredTableColumn({
      columnType: TableColumnType.ROW_DELEGATE,
      name: conditions[conditionIndex]?.name ?? '',
    }),
    getCellForFieldRow: (args) => makeConditionValueCell(args, conditionIndex),
  }));

  return [
    nameColumn,
    maybeFieldColumn,
    maybeEquipmentModelsColumn,
    defaultSetpointColumn,
    ...conditionColumns,
    maybeConfirmationMethodColumn,
  ].filter(<T,>(c: T | null): c is T => c != null);
}

/**
 * Builds a dense list of condition indices `[0..numConditions-1]` used as the
 * canonical column/index space for experimental input tables. These correspond to the indices of the rows
 * in the flowchart view.
 */
export function getDenseConditionIndices(numConditions: number): Array<number> {
  return Array.from({length: numConditions}, (_unused, index) => index);
}

interface ExperimentalInputValuesByCondition {
  conditionIndex: number;
  conditionValuesByComponent: ExperimentalInputsValuesPerCondition[number];
}

/**
 * Returns condition/value pairs for the visible condition subset, asserting those indices align with
 * the dense experimental-input values array.
 *
 * @package exported for testing.
 */
export function getExperimentalInputValuesByCondition(
  valuesByCondition: ExperimentalInputsValuesPerCondition,
  visibleConditionIndices?: ReadonlyArray<number>
): Array<ExperimentalInputValuesByCondition> {
  const conditionIndices = visibleConditionIndices ?? getDenseConditionIndices(valuesByCondition.length);
  return conditionIndices.map((conditionIndex) => {
    const conditionValuesByComponent = valuesByCondition[conditionIndex];
    assert(conditionValuesByComponent != null, 'Expected visible condition index to align with values', {
      conditionIndex,
      valuesLength: valuesByCondition.length,
      visibleConditionIndices,
    });
    return {conditionIndex, conditionValuesByComponent};
  });
}

/**
 * Returns the subset of condition indices that should render for `methodId`.
 *
 * For legacy (non-ConditionGroup) condition flows, all conditions are visible.
 * For ConditionGroup flows, a condition is visible only when the method is covered by that group's
 * `coveredMethodIds`.
 */
export function getVisibleConditionIndicesForMethod(
  conditions: ReadonlyArray<BaseCondition>,
  methodId: string
): Array<number> {
  const denseIndices = getDenseConditionIndices(conditions.length);
  if (conditions.length === 0) {
    return [];
  }
  return denseIndices.filter((denseConditionIndex) => {
    const condition = conditions[denseConditionIndex];
    assert(condition != null, 'Expected condition to be found', {
      conditionIndex: denseConditionIndex,
      conditions,
    });
    // Flows that have not been refactored to use ConditionGroup only support full bowling lane conditions
    if (!isConditionGroup(condition)) {
      return true;
    }
    const coveredMethodIds = getMethodIdsImplementingConditionGroup(condition);
    // When coveredMethodIds is null, we default to covering all methods
    return coveredMethodIds == null || coveredMethodIds.has(methodId);
  });
}

/**
 * Deep-clones per-condition experimental input typed values so table edits can
 * mutate a fresh structure without mutating state-backed arrays in place.
 */
function cloneExperimentalInputValuesPerCondition(
  valuesByCondition: ExperimentalInputsValuesPerCondition
): ExperimentalInputsValuesPerCondition {
  return valuesByCondition.map((conditionValues) => ({
    [ExperimentalInputsComponentName.PARAMETERS]: conditionValues[
      ExperimentalInputsComponentName.PARAMETERS
    ].map((fieldValues) => [...fieldValues]),
    [ExperimentalInputsComponentName.MATERIAL_INPUTS]: conditionValues[
      ExperimentalInputsComponentName.MATERIAL_INPUTS
    ].map((fieldValues) => [...fieldValues]),
    [ExperimentalInputsComponentName.EQUIPMENT]: conditionValues[
      ExperimentalInputsComponentName.EQUIPMENT
    ].map((fieldValues) => [...fieldValues]),
  }));
}

function makeParamNameCell({
  experimentalInputs,
  componentName,
  parameterIndex,
  fieldIndex,
  isVaryingParameter,
  visibleParameterIndices,
}: GetCellArgs): Cell {
  const hasFieldColumn = getHasFieldColumn(experimentalInputs, componentName, visibleParameterIndices);
  const name = experimentalInputs.parameterNames[componentName][parameterIndex];
  const formattedValue =
    shouldShowFlowchartProcedureRunDesign() && isVaryingParameter ? (
      <VaryingParameterName name={name} />
    ) : undefined;
  // Handle merge cells
  if (hasFieldColumn) {
    return fieldIndex === 0
      ? {
          text: name,
          mergeRowSpan: experimentalInputs.fields[componentName][parameterIndex].length,
          locked: true,
          formattedValue,
        }
      : {text: '', mergeRowSpan: 0, locked: true};
  } else {
    return {text: name, locked: true, formattedValue};
  }
}

function makeParamConfirmationMethodCell({
  experimentalInputs,
  componentName,
  parameterIndex,
  fieldIndex,
  method,
  shouldLockConfirmationMethod,
  visibleParameterIndices,
}: GetCellArgs): Cell {
  const hasFieldColumn = getHasFieldColumn(experimentalInputs, componentName, visibleParameterIndices);
  const confirmationMethod = experimentalInputs.parameterConfirmationMethods[componentName][parameterIndex];
  const parameterId = experimentalInputs.ids[componentName][parameterIndex];
  const baseCell: Cell = {
    ...makeConfirmationMethodCell(confirmationMethod),
    locked: shouldLockConfirmationMethod,
    customEvalArgs: [],
    customEval: (
      typedValue: TypedValue,
      columnDefinition: ColumnDefinition,
      cellDependencies: Array<TypedValue | null>,
      customEvalOptions: unknown,
      resolveFromResolutionQuery: ResolveQueryFn,
      resolveTypedValueToType: ResolveTypedValueToTypeFn
    ) => {
      const evalResult: CustomEvalResult = {val: typedValue, error: null, locked: false};
      if (componentName !== ExperimentalInputsComponentName.MATERIAL_INPUTS) {
        return evalResult;
      }
      const dataType = method.materialInputSettings.find(({version}) => version.id === parameterId)?.version
        .dataType;
      if (dataType == null) {
        logging.info('Cannot find matching material input data type', {extra: {parameterId}});
      }
      validateMaterialInputConfirmationMethod(
        evalResult,
        typedValue,
        confirmationMethod,
        dataType,
        resolveFromResolutionQuery
      );
      return evalResult;
    },
  };

  // Handle merge cells
  if (hasFieldColumn) {
    return fieldIndex === 0
      ? {...baseCell, mergeRowSpan: experimentalInputs.fields[componentName][parameterIndex].length}
      : {...baseCell, mergeRowSpan: 0};
  } else {
    return baseCell;
  }
}

function makeParamFieldCell({
  experimentalInputs,
  componentName,
  parameterIndex,
  fieldIndex,
}: GetCellArgs): Cell {
  return {
    text: experimentalInputs.fields[componentName][parameterIndex][fieldIndex].displayName,
    locked: true,
  };
}

function makeParamDefaultSetpointCell({
  experimentalInputs,
  componentName,
  parameterIndex,
  fieldIndex,
}: GetCellArgs): Cell {
  const typedValue = experimentalInputs.defaultValues[componentName][parameterIndex][fieldIndex];
  return {
    text: '',
    typedValue,
    locked: true,
    align: CELL_ALIGNMENT,
    foregroundColor: NEAR_BLACK,
    bold: false,
  };
}

function makeEquipmentModelsCell({
  experimentalInputs,
  componentName,
  parameterIndex,
  fieldIndex,
}: GetCellArgs): Cell {
  const equipmentParameterField = experimentalInputs.fields[componentName][parameterIndex][fieldIndex];
  return {
    text: '',
    typedValue: equipmentParameterField.allowedEquipmentModels ?? null,
    locked: true,
    align: CELL_ALIGNMENT,
    foregroundColor: NEAR_BLACK,
    bold: false,
  };
}

function makeConditionValueCell(
  {
    experimentalInputs,
    componentName,
    parameterIndex,
    fieldIndex,
    lockedReasonByInputId,
    isLockedPerCondition,
    isTableLocked,
    archivedEntityIds,
  }: GetCellArgs,
  conditionIndex: number
): Cell {
  const value = experimentalInputs.values[conditionIndex][componentName][parameterIndex][fieldIndex];
  const lockedReason = lockedReasonByInputId?.[experimentalInputs.ids[componentName][parameterIndex]];

  const isDefault = areTypedValuesEquivalent(
    value,
    experimentalInputs.defaultValues[componentName][parameterIndex][fieldIndex]
  );
  const isColumnLocked = isLockedPerCondition[conditionIndex];
  return {
    text: '',
    typedValue: value,
    customEvalArgs: [],
    customEval: (
      typedValue: TypedValue,
      columnDefinition: ColumnDefinition,
      cellDependencies: Array<TypedValue | null>,
      customEvalOptions: unknown,
      resolveFromResolutionQuery: ResolveQueryFn,
      resolveTypedValueToType: ResolveTypedValueToTypeFn
    ) => {
      const evalResult: CustomEvalResult = {val: typedValue, error: null, locked: false};
      const field = experimentalInputs.fields[componentName][parameterIndex][fieldIndex];

      validateNumericFieldValue(evalResult, typedValue, field, columnDefinition, resolveTypedValueToType);
      validateValueRange(evalResult, typedValue, field, columnDefinition, resolveTypedValueToType);
      validateNumberOfValues(evalResult, typedValue, field, columnDefinition, resolveTypedValueToType);
      validateEquipment(evalResult, typedValue, field, resolveFromResolutionQuery);
      if (archivedEntityIds != null) {
        validateArchivedEntity(evalResult, typedValue, archivedEntityIds);
      }
      return evalResult;
    },
    locked: lockedReason != null || isColumnLocked,
    userAnnotationIndicator:
      lockedReason && !isTableLocked && !isColumnLocked ? (
        <ErrorTriangle
          state={TriangleState.VALID_WITH_TRIANGLE}
          error={{type: '', message: lockedReason}}
          location={TriangleLocation.CELL}
        />
      ) : undefined,
    align: CELL_ALIGNMENT,
    foregroundColor: isDefault ? Color.GRAY_500 : Color.GRAY_900,
    bold: !isDefault,
  };
}

/**
 * @returns a table that can be evaluated, `null` if there are no rows to render
 */
export function convertExperimentalInputsToTable(
  experimentalInputs: ExperimentalInputsPerMethod,
  componentName: ExperimentalInputsComponentName,
  lockedReasonByInputId: Record<string, string> | null,
  conditions: Array<BaseCondition>,
  procedureType: ProcedureType,
  method: ExperimentMethod,
  isLocked: boolean,
  allowEditAllConditions: boolean = false,
  archivedEntityIds?: ReadonlySet<string>,
  visibleParameterIndices?: ReadonlyArray<number>
): Table | null {
  const numConditions = experimentalInputs.values.length;
  // An empty `conditions` falls back to dense default conditions sized off `numConditions` below; any
  // other length must already match `numConditions`, since `visibleConditionIndices` (derived from
  // `conditionsOrDefault`) indexes into `experimentalInputs.values` elsewhere
  assert(
    conditions.length === 0 || conditions.length === numConditions,
    'Expected conditions to align with experimental input values',
    {conditionsLength: conditions.length, numConditions, methodId: method.id}
  );
  const conditionsOrDefault =
    conditions.length === 0 ? getDefaultConditions(numConditions, procedureType) : conditions;
  const visibleConditionIndices = getVisibleConditionIndicesForMethod(conditionsOrDefault, method.id);
  const parameterNames = experimentalInputs.parameterNames[componentName];
  const numParameters = parameterNames.length;
  if (numParameters === 0) {
    return null;
  }
  const fields = experimentalInputs.fields[componentName];
  const parameterIndices = visibleParameterIndices ?? getDenseParameterIndices(numParameters);
  assert(
    parameterIndices.every((parameterIndex) => parameterIndex >= 0 && parameterIndex < numParameters),
    'Expected visible parameter indices to align with experimental input parameters',
    {visibleParameterIndices, numParameters, componentName, methodId: method.id}
  );
  if (parameterIndices.length === 0) {
    return null;
  }

  const rowLabels = parameterIndices.map((_parameterIndex) => '');

  const columnHandlers = getColumnHandlers(conditionsOrDefault, visibleConditionIndices, experimentalInputs, {
    componentName,
    procedureType,
    visibleParameterIndices,
  });
  const columnLabels = columnHandlers.map(({label}) => label);
  const columnConstraints = columnHandlers.map(({structuredTableColumn}) =>
    toColumnConstraint({structuredTableColumn, schemas: []})
  );

  const varyingParameterIndices = shouldShowFlowchartProcedureRunDesign()
    ? new Set(getVaryingParameterIndices(experimentalInputs, componentName, visibleConditionIndices))
    : new Set<number>();
  const rows: Array<Array<Cell>> = Array.from(
    enumTableRows(fields, visibleParameterIndices),
    ({parameterIndex, fieldIndex}) =>
      columnHandlers.map(({getCellForFieldRow}) =>
        getCellForFieldRow({
          experimentalInputs,
          componentName,
          parameterIndex,
          fieldIndex,
          lockedReasonByInputId,
          method,
          // For plans (allowEditAllConditions=true), all conditions are editable.
          // For runs, only new conditions (without id) are editable.
          isLockedPerCondition: allowEditAllConditions
            ? conditionsOrDefault.map(() => false)
            : conditionsOrDefault.map((c) => isPersistedCondition(c)),
          shouldLockConfirmationMethod: allowEditAllConditions
            ? false
            : conditionsOrDefault.some((c) => isPersistedCondition(c)),
          isTableLocked: isLocked,
          isVaryingParameter: varyingParameterIndices.has(parameterIndex),
          archivedEntityIds,
          visibleParameterIndices,
        })
      )
  );

  const delegatedColumnConstraintByValueIndex: Array<Array<ColumnConstraint>> = getDelegatedColumnConstraints(
    experimentalInputs,
    componentName
  );

  const rowData: Array<RowData> = Array.from(
    enumTableRows(fields, visibleParameterIndices),
    ({parameterIndex, fieldIndex}) => ({
      delegatedColumnConstraints: new Map(
        columnConstraints.map((col) => [
          col.structuredTableColumn!.provenanceUuid,
          delegatedColumnConstraintByValueIndex[parameterIndex][fieldIndex],
        ])
      ),
    })
  );

  const numTrailing = getNumTrailingColumns(componentName);
  const numPrefixColumns = columnHandlers.length - visibleConditionIndices.length - numTrailing;

  return {
    name: TABLE_NAME_BY_COMPONENT_NAME[componentName],
    _firstColumnWidth: 0,
    widths: columnLabels.map(() => 120),
    rowLabels,
    columnLabels,
    columnConstraints,
    headerCol: false,
    headerRow: false,
    table: rows,
    lockedDims: true,
    allowColumnEdits: false,
    rowData,
    defaultAffixedColumnCount: numPrefixColumns,
  };
}

function getDelegatedColumnConstraints(
  experimentalInputs: ExperimentalInputsPerMethod,
  componentName: ExperimentalInputsComponentName
): Array<Array<ColumnConstraint>> {
  const delegatedColumnConstraints: Array<Array<ColumnConstraint>> = fillArray(
    experimentalInputs.parameterNames[componentName].length,
    (parameterIndex) =>
      experimentalInputs.fields[componentName][parameterIndex].map((field) => {
        const isMultiValue =
          field.schemaField.isMulti ||
          (field.allowMultiValueForPlateMap &&
            experimentalInputs.parameterConfirmationMethods[componentName][parameterIndex] ===
              ProcedureParameterConfirmationMethod.PLATE_MAP);
        const schemaField: SchemaField = {...field.schemaField, isMulti: isMultiValue};
        const selectorsById = {};
        const structuredTableColumn = makeSchemaFieldStructuredColumn(schemaField, selectorsById);
        const columnDefinition = toSchemaFieldColumnDefinition(schemaField, structuredTableColumn);
        return {structuredTableColumn, columnDefinition};
      })
  );
  return delegatedColumnConstraints;
}
